import type { Collection, Watch } from '@/types'

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  SEARCH — the most-used tool on a watch shop                             ║
 * ╠══════════════════════════════════════════════════════════════════════════╣
 * ║  Rules this engine follows, in order of importance:                       ║
 * ║                                                                          ║
 * ║  1. ACCENTS NEVER MATTER. "heritage" finds "Héritage"; "co dien" finds    ║
 * ║     "Cổ điển". Folding works both directions, both languages.            ║
 * ║  2. PUNCTUATION NEVER MATTERS IN A REFERENCE. "ta30024", "TA-300.24" and  ║
 * ║     "ta 300 24" are the same query.                                       ║
 * ║  3. EVERY WORD MUST MATCH SOMETHING (AND, not OR). "gold chrono" must not ║
 * ║     return every gold watch — that is how a search loses trust.           ║
 * ║  4. WHERE a word matched decides the ranking: a hit in the reference or   ║
 * ║     the name outranks a hit buried in the description.                    ║
 * ║  5. The engine reports WHICH ranges matched, so the interface can         ║
 * ║     highlight them and the shopper can see why a result is there.         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

/** Fold one character: lowercase, strip its accent, and map đ → d. */
function foldChar(ch: string): string {
  return ch
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/đ/g, 'd')
}

export function fold(value: string): string {
  let out = ''
  for (const ch of value) out += foldChar(ch)
  return out
}

/**
 * Fold a string while remembering where every folded character came from, so a
 * match found in the folded text can be highlighted in the ORIGINAL text.
 * Needed because stripping accents changes string length ("é" is two code
 * points in NFD), and naive index maths would highlight the wrong letters.
 */
function foldWithMap(value: string): { folded: string; map: number[] } {
  let folded = ''
  const map: number[] = []
  let i = 0
  for (const ch of value) {
    for (const f of foldChar(ch)) {
      folded += f
      map.push(i)
    }
    i += ch.length
  }
  return { folded, map }
}

/** Everything that is not a letter or a digit is a word break. */
export function tokenize(query: string): string[] {
  return fold(query)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/** Squash a reference down to letters and digits: "TA-300.24" → "ta30024". */
function compact(value: string): string {
  return fold(value).replace(/[^a-z0-9]/g, '')
}

// ── Scoring ─────────────────────────────────────────────────────────────────
// The numbers are relative, not absolute. What matters is the ORDER they
// encode: reference → name → maison → collection → specification → prose.

const SCORE = {
  refExact: 200,
  refPartial: 120,
  titleWord: 70,
  titleAny: 45,
  maison: 30,
  collection: 26,
  spec: 14,
  body: 7,
  /** The whole query appears in the name, in order — almost certainly the one. */
  phrase: 60,
  featured: 5,
}

interface Haystack {
  refCompact: string
  title: string
  maison: string
  collection: string
  spec: string
  body: string
}

function haystackOf(watch: Watch, collectionNames: string): Haystack {
  const specs = Array.isArray(watch.specs) ? watch.specs : []
  return {
    refCompact: compact(watch.reference ?? ''),
    // Both languages of the name are searchable, whichever one is on screen.
    title: fold([watch.title, watch.title_vi].filter(Boolean).join(' ')),
    maison: fold(watch.maison ?? ''),
    collection: fold(`${watch.collection ?? ''} ${collectionNames}`),
    spec: fold(
      [
        watch.movement,
        watch.condition,
        watch.case_material,
        watch.case_size,
        watch.water_resistance,
        watch.strap,
        watch.year,
        ...specs.map((s) => `${s.label} ${s.value}`),
      ]
        .filter(Boolean)
        .join(' '),
    ),
    body: fold([watch.description, watch.description_vi].filter(Boolean).join(' ')),
  }
}

/** Does the token start a word in this text? "chrono" in "C1 Chronograph". */
function hasWordStart(text: string, token: string): boolean {
  let from = 0
  for (;;) {
    const at = text.indexOf(token, from)
    if (at === -1) return false
    if (at === 0 || !/[a-z0-9]/.test(text[at - 1])) return true
    from = at + 1
  }
}

/** Score one watch against one token. 0 means "this word matched nothing". */
function scoreToken(hay: Haystack, token: string): number {
  if (hay.refCompact) {
    if (hay.refCompact === token) return SCORE.refExact
    if (hay.refCompact.includes(token) && token.length >= 2) return SCORE.refPartial
  }
  if (hasWordStart(hay.title, token)) return SCORE.titleWord
  if (hay.title.includes(token)) return SCORE.titleAny
  if (hay.maison.includes(token)) return SCORE.maison
  if (hay.collection.includes(token)) return SCORE.collection
  if (hay.spec.includes(token)) return SCORE.spec
  if (hay.body.includes(token)) return SCORE.body
  return 0
}

export interface SearchHit {
  watch: Watch
  score: number
}

/**
 * Rank `rows` against `query`. Returns only watches where EVERY word of the
 * query matched somewhere, best first. An empty query returns nothing — the
 * caller decides what an empty search box should show.
 */
export function searchWatches(
  rows: Watch[],
  query: string,
  options: { collections?: Collection[]; limit?: number } = {},
): SearchHit[] {
  const tokens = tokenize(query)
  if (!tokens.length) return []

  // Collection names in every language, so "lặn biển" finds the dive pieces.
  const namesBySlug = new Map<string, string>()
  for (const c of options.collections ?? []) {
    namesBySlug.set(c.slug, [c.name, c.name_vi, c.tagline, c.tagline_vi].filter(Boolean).join(' '))
  }

  const phrase = fold(query.trim())
  const hits: SearchHit[] = []

  for (const watch of rows) {
    const hay = haystackOf(watch, namesBySlug.get(watch.collection) ?? '')
    let total = 0
    let matchedAll = true

    for (const token of tokens) {
      const s = scoreToken(hay, token)
      if (s === 0) {
        matchedAll = false
        break
      }
      total += s
    }
    if (!matchedAll) continue

    if (tokens.length > 1 && hay.title.includes(phrase)) total += SCORE.phrase
    if (watch.is_featured) total += SCORE.featured

    hits.push({ watch, score: total })
  }

  hits.sort(
    (a, b) =>
      b.score - a.score ||
      new Date(b.watch.created_at ?? 0).getTime() - new Date(a.watch.created_at ?? 0).getTime(),
  )

  return typeof options.limit === 'number' ? hits.slice(0, options.limit) : hits
}

// ── Highlighting ────────────────────────────────────────────────────────────

export interface Segment {
  text: string
  hit: boolean
}

/**
 * Split `text` into plain and matched segments for the query, so the interface
 * can show the shopper exactly which letters it matched.
 */
export function highlight(text: string, query: string): Segment[] {
  const tokens = tokenize(query)
  if (!text || !tokens.length) return [{ text, hit: false }]

  const { folded, map } = foldWithMap(text)
  const ranges: [number, number][] = []

  for (const token of tokens) {
    let from = 0
    for (;;) {
      const at = folded.indexOf(token, from)
      if (at === -1) break
      const start = map[at]
      const end = at + token.length - 1 < map.length ? map[at + token.length - 1] + 1 : text.length
      ranges.push([start, end])
      from = at + token.length
    }
  }
  if (!ranges.length) return [{ text, hit: false }]

  // Merge overlaps so two tokens hitting the same word render as one mark.
  ranges.sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = [ranges[0]]
  for (const [s, e] of ranges.slice(1)) {
    const last = merged[merged.length - 1]
    if (s <= last[1]) last[1] = Math.max(last[1], e)
    else merged.push([s, e])
  }

  const out: Segment[] = []
  let cursor = 0
  for (const [s, e] of merged) {
    if (s > cursor) out.push({ text: text.slice(cursor, s), hit: false })
    out.push({ text: text.slice(s, e), hit: true })
    cursor = e
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), hit: false })
  return out
}

// ── Recent searches ─────────────────────────────────────────────────────────
// Per browser, never sent anywhere. Short list: an inbox of old queries is
// clutter, the last five are a shortcut.

const RECENT_KEY = 'tempora-recent-searches'
const RECENT_MAX = 5

export function readRecentSearches(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
    return Array.isArray(raw) ? raw.filter((s) => typeof s === 'string').slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

export function pushRecentSearch(query: string): string[] {
  const q = query.trim()
  if (q.length < 2) return readRecentSearches()
  const next = [q, ...readRecentSearches().filter((s) => fold(s) !== fold(q))].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    /* storage blocked — the shortcut simply won't persist */
  }
  return next
}

export function clearRecentSearches(): string[] {
  try {
    localStorage.removeItem(RECENT_KEY)
  } catch {
    /* ignore */
  }
  return []
}
